import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import Header from "../Header/Header";
import FooterWithImage from "../FooterWithImage/FooterWithImage";
import buildQuotationPdfData from "../../utils/buildQuotationPdfData";
import { buildAceQuotationPdfRows } from "../../utils/aceQuotationPdfTemplate";
import { formatInr } from "../../utils/quotationPdfTotals";
import { getQuotationFromLine, getQuotationToLine } from "../../utils/quotationRouteLines";
import signImage from "../../assets/sign.jpg";
import { readTenantBranding } from "../../utils/tenantBrandingCache";
import { pdfSignatureSrcFromBranding } from "../../utils/pdfFooterBranding";

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 10,
    paddingTop: 28,
    paddingBottom: 32,
    paddingHorizontal: 48,
    lineHeight: 1.45,
  },
  title: {
    fontSize: 15,
    color: "red",
    textAlign: "center",
    marginBottom: 10,
    fontWeight: "bold",
  },
  /** Ref no. | Customer | Date — three equal columns aligned with pricing form */
  metaRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#ddd",
    padding: 8,
    borderRadius: 2,
  },
  metaCol: { width: "31%" },
  contactBlock: {
    marginBottom: 10,
    paddingLeft: 2,
  },
  small: { fontSize: 8, color: "#333", marginBottom: 2 },
  dear: { marginBottom: 6 },
  intro: { marginBottom: 10, textAlign: "left" },
  fromTo: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#ccc",
    padding: 8,
  },
  fromToCol: { width: "48%" },
  fromToLabel: { fontSize: 9, fontWeight: "bold", marginBottom: 4 },
  summaryLine: { fontSize: 9, marginBottom: 6 },
  tableHeader: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#000",
    paddingVertical: 4,
    marginTop: 4,
  },
  descCol: { width: "72%", paddingRight: 6 },
  amtCol: { width: "28%" },
  amtText: { textAlign: "right" },
  chargeRow: {
    flexDirection: "row",
    borderBottomWidth: 0.5,
    borderColor: "#ddd",
    paddingVertical: 4,
    minHeight: 16,
  },
  sectionLabel: { fontWeight: "bold" },
  subIndent: { paddingLeft: 14 },
  bold: { fontWeight: "bold" },
  noteBlock: { marginTop: 8, fontSize: 9 },
  custAckBlock: {
    marginTop: 14,
    padding: 10,
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 2,
  },
  custAckTitle: { fontSize: 10, fontWeight: "bold", marginBottom: 6 },
  custAckImg: { width: 140, height: 56, objectFit: "contain", marginBottom: 4 },
  custAckMeta: { fontSize: 8, color: "#444" },
  signBlock: { marginTop: 24 },
  signImage: { width: 112, height: 44, objectFit: "contain", marginBottom: 6 },
  signRow: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" },
  termsPageTitle: { fontSize: 12, fontWeight: "bold", marginBottom: 8, color: "red" },
  termLine: { marginBottom: 4, paddingLeft: 8, fontSize: 9 },
  /**
   * Wrapper + array children: JSX whitespace becomes TextInstance nodes; those have no yoga layout
   * and break insertChild (Yoga "Type mismatch") under React 16 + @react-pdf/renderer v1.
   */
  pageBody: { flexDirection: "column", flexGrow: 1 },
});

/**
 * @param {{ quotation: object, items?: array }} props.data — GET /quotations/:id shape
 */
const QuotationPDF = ({ data }) => {
  const d = buildQuotationPdfData(data);
  const { quotation, items, vehicles, intro, paymentTerms, terms } = d;
  const termLines = Array.isArray(terms) ? terms : [];
  const q = quotation;

  const refNo = q.quotationRefNo || "—";
  const branding = readTenantBranding() || {};
  const signLabel = branding.companyName || "ACE Relocations";
  const signatoryImage = pdfSignatureSrcFromBranding(branding, signImage);
  const qDate = q.quotationDate || (q.createdAt ? new Date(q.createdAt).toLocaleDateString("en-IN") : "—");
  const fromLine = getQuotationFromLine(q);
  const toLine = getQuotationToLine(q);

  const inventorySummary =
    items && items.length
      ? `${items.length} line(s), total qty ${q.totalQuantity ?? "—"}, total CFT ${q.totalCft ?? "—"}`
      : "As per attached / discussed scope.";

  const aceRows = buildAceQuotationPdfRows(q);

  const chargeRowViews = aceRows.map((row, idx) => {
    const descStyle = row.section
      ? styles.sectionLabel
      : row.bold
      ? styles.bold
      : row.info
      ? { fontSize: 9 }
      : {};
    const rowStyle = row.indent ? { ...styles.chargeRow, ...styles.subIndent } : styles.chargeRow;
    const amt = row.amount != null && row.amount !== "" ? String(row.amount) : "";
    return (
      <View key={`r-${idx}`} style={rowStyle}>
        <View style={styles.descCol}>
          <Text style={descStyle}>{String(row.desc ?? "")}</Text>
        </View>
        <View style={styles.amtCol}>
          <Text style={styles.amtText}>{amt}</Text>
        </View>
      </View>
    );
  });

  const page1Inner = [
    <Header key="hdr" />,
    <Text key="ti" style={styles.title}>
      Quotation
    </Text>,
    <View key="c1" style={styles.metaRow}>
      <View style={styles.metaCol}>
        <Text style={styles.small}>Ref no.</Text>
        <Text style={styles.bold}>{refNo}</Text>
      </View>
      <View style={styles.metaCol}>
        <Text style={styles.small}>Customer name</Text>
        <Text style={styles.bold}>{q.customerName || "—"}</Text>
      </View>
      <View style={styles.metaCol}>
        <Text style={styles.small}>Date</Text>
        <Text style={styles.bold}>{qDate}</Text>
      </View>
    </View>,
    q.companyName || q.email || q.phoneNumber ? (
      <View key="c1b" style={styles.contactBlock}>
        {q.companyName ? <Text style={{ fontSize: 9 }}>{q.companyName}</Text> : null}
        {q.email ? <Text style={{ fontSize: 9 }}>{q.email}</Text> : null}
        {q.phoneNumber ? <Text style={{ fontSize: 9 }}>Ph: {q.phoneNumber}</Text> : null}
      </View>
    ) : null,
    <Text key="dear" style={styles.dear}>
      Dear Sir,
    </Text>,
    <Text key="in" style={styles.intro}>
      {intro}
    </Text>,
    <View key="ft" style={styles.fromTo}>
      <View style={styles.fromToCol}>
        <Text style={styles.fromToLabel}>From</Text>
        {String(fromLine)
          .split("\n")
          .map((line, i) => (
            <Text key={`fl-${i}`}>{line}</Text>
          ))}
      </View>
      <View style={styles.fromToCol}>
        <Text style={styles.fromToLabel}>To</Text>
        {String(toLine)
          .split("\n")
          .map((line, i) => (
            <Text key={`tl-${i}`}>{line}</Text>
          ))}
      </View>
    </View>,
    <Text key="sum" style={styles.summaryLine}>
      Inventory: {inventorySummary}
      {q.declaredLuggageValue != null ? `  |  Declared value: Rs. ${formatInr(q.declaredLuggageValue)}` : ""}
    </Text>,
    ...(vehicles && vehicles.length
      ? [
          <Text key="veh" style={styles.summaryLine}>
            Vehicles: {vehicles.filter(Boolean).join("; ")}
          </Text>,
        ]
      : []),
    <View key="th" style={styles.tableHeader}>
      <View style={styles.descCol}>
        <Text style={styles.bold}>Description</Text>
      </View>
      <View style={styles.amtCol}>
        <Text style={[styles.bold, styles.amtText]}>Amount (Rs.)</Text>
      </View>
    </View>,
    ...chargeRowViews,
    ...(q.validityDays != null || q.moveWindowDays != null || q.transitTimeNote
      ? [
          <Text key="val" style={[styles.noteBlock, { marginTop: 6 }]}>
            {q.validityDays != null ? `Validity: ${q.validityDays} days. ` : ""}
            {q.moveWindowDays != null ? `Move window: ${q.moveWindowDays} days. ` : ""}
            {q.transitTimeNote || ""}
          </Text>,
        ]
      : []),
    <View key="pay" style={styles.noteBlock}>
      <Text style={styles.bold}>Payment terms</Text>
      <Text>{String(paymentTerms || "")}</Text>
    </View>,
    ...(q.quotationPdfNote || q.notes
      ? [
          <View key="qn" style={styles.noteBlock}>
            <Text style={styles.bold}>Note</Text>
            <Text>{[q.quotationPdfNote, q.notes].filter(Boolean).map(String).join("\n\n")}</Text>
          </View>,
        ]
      : []),
    ...(q.customerSignaturePng && String(q.customerSignaturePng).startsWith("data:image/png")
      ? [
          <View key="custAck" style={styles.custAckBlock}>
            <Text style={styles.custAckTitle}>Customer acknowledgment (e-sign)</Text>
            <Image src={q.customerSignaturePng} style={styles.custAckImg} />
            <Text>{q.customerSignerName || "—"}</Text>
            <Text style={styles.custAckMeta}>
              {q.customerSignedAt ? new Date(q.customerSignedAt).toLocaleString("en-IN") : ""}
            </Text>
          </View>,
        ]
      : []),
    <View key="sig" style={styles.signBlock}>
      <Image src={signatoryImage} style={styles.signImage} />
      <View style={styles.signRow}>
        <Text>Thanking you,</Text>
        <Text style={styles.bold}>For {signLabel}</Text>
      </View>
    </View>,
    <FooterWithImage key="foot" />,
  ];

  const page2Inner = [
    <Header key="h2" />,
    <Text key="tc" style={styles.termsPageTitle}>
      Terms &amp; Conditions
    </Text>,
    ...termLines.map((line, i) => (
      <Text key={`t-${i}`} style={styles.termLine}>
        {i + 1}. {String(line)}
      </Text>
    )),
    <FooterWithImage key="f2" />,
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}><View style={styles.pageBody}>{page1Inner}</View></Page>
      <Page size="A4" style={styles.page}><View style={styles.pageBody}>{page2Inner}</View></Page>
    </Document>
  );
};

export default QuotationPDF;
